import { useEffect, useState } from "react";
import { X, Users, DoorOpen, Clock, FileSignature, ExternalLink } from "lucide-react";
import api from "../lib/api";
import MemberDetail from "./MemberDetail";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`);
const fmtD = (iso) => { if (!iso) return "—"; const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); };

export const statusKey = (o) => (o.status === "available" && o.upcoming_occupant ? "signed" : o.status);

function useMember(o, location) {
  const [m, setM] = useState(undefined);
  const name = o.occupant;
  useEffect(() => {
    if (!name) { setM(null); return; }
    api.get("/members", { params: { q: String(o.office_number), location } })
      .then(({ data }) => setM(data.members.find((x) => x.offices.some((f) => String(f.number) === String(o.office_number))) || null))
      .catch(() => setM(null));
  }, [name, o.office_number, location]);
  return m;
}

function Pricing({ o }) {
  return (
    <div className="grid grid-cols-3 gap-1 text-center rounded-lg bg-brand-page border border-brand-border py-2">
      {[["M2M", o.monthly_price], ["6-mo", o.price_6mo], ["12-mo", o.price_12mo]].map(([l, v]) => (
        <div key={l}><p className="text-[9px] uppercase tracking-wide text-brand-tertiary">{l}</p><p className="font-mono text-sm text-brand-ink">{money(v)}</p></div>
      ))}
    </div>
  );
}

export function OfficePopover({ o, location, side, onClose }) {
  const st = statusKey(o);
  const m = useMember(o, location);
  const [open, setOpen] = useState(null);
  const head = { available: ["Available", "text-emerald-700", DoorOpen], signed: ["Signed — future move-in", "text-sky-700", FileSignature],
    notice: ["On notice", "text-amber-700", Clock], occupied: ["Rented", "text-rose-700", Users] }[st] || ["", "", Users];
  const Icon = head[2];

  return (
    <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}
      className={`absolute z-30 w-72 max-sm:!fixed max-sm:!inset-x-3 max-sm:!bottom-3 max-sm:!top-auto max-sm:!left-3 max-sm:!right-3 max-sm:!w-auto max-sm:z-50 rounded-xl border border-brand-border bg-brand-surface shadow-2xl p-4 text-left animate-in fade-in zoom-in-95 duration-150 ${side}`}
      data-testid={`fp-popover-${o.office_number}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-serif text-xl text-brand-ink">Office {o.office_number}</p>
          <p className="text-[11px] text-brand-tertiary">{o.desks ? `${o.desks} desk${o.desks > 1 ? "s" : ""}` : "—"}{o.window ? " · window" : ""}</p>
        </div>
        <button onClick={onClose} data-testid="fp-popover-close"><X className="h-4 w-4 text-brand-muted" /></button>
      </div>
      <p className={`mt-2 text-xs font-medium flex items-center gap-1.5 ${head[1]}`}><Icon className="h-3.5 w-3.5" /> {head[0]}</p>

      {(st === "occupied" || st === "notice") && (
        <div className="mt-3 space-y-2" data-testid="fp-popover-occupant">
          <p className="text-sm text-brand-ink font-medium">{o.occupant || "Occupied"}</p>
          {st === "notice" && <p className="text-xs text-amber-800">Leaving — available {fmtD(o.notice_date)}</p>}
          {m === undefined && <p className="text-xs text-brand-tertiary">Loading member…</p>}
          {m && (
            <div className="text-xs text-brand-muted space-y-1">
              {(m.primary_contact?.name || m.contacts[0]) && <p>Contact: <span className="text-brand-ink">{m.primary_contact?.name || m.contacts[0]?.name}</span>{(m.primary_contact?.email || m.contacts[0]?.email) ? ` · ${m.primary_contact?.email || m.contacts[0]?.email}` : ""}</p>}
              <p>{m.contact_count} people · {money(m.mrr)}/mo{m.billing?.delinquent ? " · " : ""}{m.billing?.delinquent && <span className="text-rose-600">delinquent</span>}</p>
              <button onClick={() => setOpen(m.company_id)} data-testid="fp-popover-open-member" className="inline-flex items-center gap-1 text-brand-primary hover:underline">Open member profile <ExternalLink className="h-3 w-3" /></button>
            </div>
          )}
        </div>
      )}

      {st === "signed" && (
        <div className="mt-3 rounded-lg bg-sky-50 border border-sky-200 px-3 py-2 text-xs text-sky-900" data-testid="fp-popover-signed">
          <span className="font-medium">{o.upcoming_occupant}</span> moves in {fmtD(o.upcoming_move_in)}. Free until then.
        </div>
      )}

      {(st === "available" || st === "signed" || st === "notice") && <div className="mt-3" data-testid="fp-popover-pricing"><Pricing o={o} /></div>}
      {open && <MemberDetail companyId={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
